import React, { useCallback, useEffect, useState } from 'react';
import { Bell, CircleHelp, LogOut, Menu, QrCode, UserCircle2 } from "lucide-react";
import sessionDB from "../../utils/sessionDB";
import { Trans, useTranslation } from "react-i18next";
import { LanguageSelector } from "./LanguageSelector";
import { UserPreferencesModal } from "./UserPreferencesModal";
import { NotificationsModal } from "./NotificationsModal";
import { FaqModal } from "./FaqModal";
import { notificationService } from "../../services/notificationService";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import authApi from "../../utils/api";

export function Header({ showMenuToggle = true, showBottomBorder = true }) {
  const { t } = useTranslation();
  const tenant = sessionDB.getTenantObj();
  const tenantName = tenant.tenantName || "";
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState("");
  const [logoutPending, setLogoutPending] = useState(false);

  const handleLogout = async () => {
    if (logoutPending) return;

    setLogoutPending(true);
    const refreshToken = sessionDB.getRefreshToken();

    try {
      await fetch(authApi._url("/saas/login/logout"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // The local session still needs to be removed when the server cannot be
      // reached; the refresh token will no longer be reused by this browser.
    } finally {
      // Signing out locally must succeed even when the logout endpoint is
      // unavailable. The event also resets App's in-memory session state.
      sessionDB.clearSession();
      window.dispatchEvent(new CustomEvent("hrms-logout"));
    }
  };

  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      const value = response?.data?.count ?? response?.data?.unreadCount ?? response?.count ?? response?.unreadCount ?? response?.data ?? response ?? 0;
      setUnreadCount(Math.max(0, Number(value) || 0));
    } catch {
      // A count failure should not disrupt the rest of the global header.
    }
  }, []);

  useEffect(() => { loadUnreadCount(); }, [loadUnreadCount]);

  useEffect(() => {
    return () => {
      if (qrImageUrl) URL.revokeObjectURL(qrImageUrl);
    };
  }, [qrImageUrl]);

  const loadQrCode = useCallback(async () => {
    setQrLoading(true);
    setQrError("");
    try {
      const response = await authApi._fetchWithRefresh(() =>
        fetch(authApi._url("/saas/login/get/qrcode"), {
          method: "GET",
          headers: authApi._headers("LOGIN_REQ", { Accept: "image/png" }),
        })
      );
      if (!response.ok) throw new Error(`Unable to load QR code (${response.status})`);

      const blob = await response.blob();
      if (blob.type && blob.type !== "image/png") throw new Error("The QR code response is not a PNG image.");
      setQrImageUrl(URL.createObjectURL(blob));
    } catch (error) {
      setQrError(error?.message || "Unable to load QR code.");
    } finally {
      setQrLoading(false);
    }
  }, []);

  const handleQrOpenChange = (open) => {
    setQrOpen(open);
    if (open && !qrImageUrl && !qrLoading) loadQrCode();
  };

  return (
    <header 
    className={`hrms-shell__header relative !grid !h-auto min-h-[112px] w-full grid-cols-1 grid-rows-[48px_32px] items-center gap-y-2 !px-4 !py-2 sm:!px-6 bg-[var(--color-primary)] shrink-0 ${
      showBottomBorder ? "border-b border-[#1E40AF]" : "border-b-0"
    }`}
    style={showBottomBorder ? undefined : { borderBottom: "none" }}>
      <div className="col-start-1 row-start-1 flex min-w-0 items-center justify-self-start self-start gap-2 sm:gap-4">
          <div className="h-9 w-9 rounded-[8px] overflow-hidden shrink-0 bg-white sm:h-12 sm:w-12">
                <img
                  src="/images/karya1.1.jpg"
                  alt={t("common.companyLogo")}
                  className="h-full w-full object-cover block" />
          </div>
           <div className="flex h-9 flex-col justify-center leading-none min-w-0 sm:h-12">
                <span className="text-lg font-bold text-white truncate sm:text-2xl"><Trans i18nKey="common.karya" /></span>
                <span className="mt-0.5 text-[8px] uppercase tracking-[0.2em] text-white/70 sm:text-[10px] sm:tracking-[0.3em]">
                  <Trans i18nKey="common.saas" />
                </span>
            </div>
      </div>
      <div className="col-start-1 row-start-2 flex h-8 items-center justify-self-start self-end">
        {showMenuToggle && <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent("toggle-sidebar"))}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/40 cursor-pointer"
          aria-label={t("common.openMenu")}
        >
          <Menu className="h-5 w-5" />
        </button>}
      </div>

      {/* Centered tenant name, absolutely positioned so it's centered on the whole header — only visible on desktop */}
      <div className="pointer-events-none absolute left-1/2 top-8 hidden -translate-x-1/2 -translate-y-1/2 md:block">
        <span className="whitespace-nowrap text-3xl font-bold leading-none text-white">
         {tenantName}
        </span>
      </div>
        
      <div className="absolute right-4 top-2 flex min-w-0 justify-end sm:right-6">
        <img
          src={tenant.tenantLogo}
          alt={tenantName || "Tenant logo"}
          className="h-9 w-auto max-w-[120px] shrink-0 object-contain sm:h-12 sm:max-w-[180px]"
        />
      </div>
      <div className="absolute bottom-2 right-4 flex h-8 items-center justify-end gap-1.5 sm:right-6 [&_select]:h-8 [&_select]:px-1.5 [&_select]:text-xs">
        <button
          type="button"
          onClick={() => setFaqOpen(true)}
          className="grid h-8 w-8 place-items-center rounded-full text-white hover:bg-[var(--color-workspace)]/40"
          aria-label="Open help and FAQs"
          title="Help & FAQs"
        >
          <CircleHelp className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setNotificationsOpen(true)}
          className="relative grid h-8 w-8 place-items-center rounded-full hover:bg-[var(--color-workspace)]/40"
          aria-label={t("common.notifications")}
        >
          <Bell className="h-4 w-4 text-white" />
          {unreadCount > 0 && <span className="absolute -right-1 -top-1 grid min-h-4 min-w-4 place-items-center rounded-full bg-[var(--color-error)] px-1 text-[9px] font-bold leading-none text-white ring-2 ring-[var(--color-primary)]" aria-label={t("notifications.unreadCount", { count: unreadCount })}>{unreadCount > 99 ? "99+" : unreadCount}</span>}
        </button>
       
        <div className="hidden md:block">
          <Popover open={qrOpen} onOpenChange={handleQrOpenChange}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="grid h-8 w-8 place-items-center rounded-full text-white hover:bg-[var(--color-workspace)]/40"
                aria-label="Show QR code"
              >
                <QrCode className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="center" sideOffset={8} className="w-48 bg-white p-3 text-center">
              {qrLoading && <div className="py-12 text-xs text-slate-500">Loading QR code...</div>}
              {qrError && (
                <div className="py-4">
                  <p className="text-xs text-red-600">{qrError}</p>
                  <button type="button" onClick={loadQrCode} className="mt-3 rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white">
                    Retry
                  </button>
                </div>
              )}
              {qrImageUrl && !qrLoading && !qrError && (
                <img src={qrImageUrl} alt="QR code" className="mx-auto h-40 w-40 object-contain" />
              )}
            </PopoverContent>
          </Popover>
        </div>
        <button
          type="button"
          onClick={() => setPreferencesOpen(true)}
          className="flex h-8 min-w-0 items-center gap-1.5 rounded-lg px-1.5 text-xs text-white hover:bg-[var(--color-workspace)]/40"
          aria-label="Open user preferences"
        >
          <UserCircle2 className="h-4 w-4 shrink-0" />
          {/*<span className="hidden max-w-28 truncate xl:block" title={`${emp.first_name || ""} ${emp.last_name || ""}`.trim()}>
            {emp.first_name || emp.last_name ? `${emp.first_name || ""} ${emp.last_name || ""}` : initials}
          </span>*/}
        </button>
        <LanguageSelector />
        <button
          type="button"
          onClick={handleLogout}
          disabled={logoutPending}
          aria-busy={logoutPending}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-rose-200 hover:bg-[var(--color-workspace)]/40 hover:text-white"
          title={t("common.signOut")}
          aria-label={t("common.signOut")}
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
      <UserPreferencesModal open={preferencesOpen} onOpenChange={setPreferencesOpen} />
      <FaqModal open={faqOpen} onOpenChange={setFaqOpen} />
      <NotificationsModal open={notificationsOpen} onOpenChange={setNotificationsOpen} onUnreadCountChange={setUnreadCount} />
    </header>
  );
}
